import {
  actions,
  connectApiFetchOptions,
  isMobileBrowser,
  getUrlQueryParams,
  ApplicationStorage,
  UI_FLOWS,
  STATUS_CODES,
} from '@americanexpress/amex-connect-core';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import { fromJS } from 'immutable';

import { mockSendPartnerToken, mockValidateUrl } from './mockApi';
import { updateUserSession } from './userSession';

const sendPartnerToken = async ({
  authCode, correlationId, dispatch, getState,
}) => {
  const state = getState();
  const options = connectApiFetchOptions({
    apiName: 'partnertoken',
    credentials: 'same-origin',
  }, state, correlationId);
  options.init.method = 'post';
  options.init.body = JSON.stringify({ auth_code: authCode });

  const userSessionApiPath = state.getIn(['config', 'functionsUrl']);

  try {
    const responses = await Promise.all([
      updateUserSession(userSessionApiPath),
      fetch(options.url, options.init),
    ]);
    const data = await responses[1].json();

    if (!responses[1].ok || data.status_code !== '0000') {
      dispatch(actions.mergeValues(fromJS({
        isAccountsLoaded: true,
        isAuthenticated: responses[0].ok,
        currentFlow: UI_FLOWS.ERROR,
        error: STATUS_CODES.SYSTEM,
      })));
      throw new Error(`partnerToken API failed with status ${responses[1].status}`);
    }

    if (responses[1].ok) {
      dispatch(actions.mergeValues(fromJS({
        isAuthenticated: responses[0].ok,
        correlationId: responses[1].headers.get('x-amex-tracking-id'),
        splunkToken: data.access_token,
      })));
    }
  } catch (e) {
    dispatch(actions.mergeValues(fromJS({
      isAuthenticated: false,
      isAccountsLoaded: true,
      currentFlow: UI_FLOWS.ERROR,
      error: STATUS_CODES.SYSTEM,
    })));
    dispatch(addErrorToReport(e, {
      collectionMethod: 'partnerToken API - Exception',
      moduleID: 'amex-connect-page-wrapper',
    }));
  }
};

export const apiSendPartnerToken = async ({
  authCode, correlationId, dispatch, getState,
}) => {
  dispatch(actions.setLoading(true));
  /* istanbul ignore next */
  if (authCode === 'mockCode') {
    await mockSendPartnerToken(dispatch);
  } else {
    await sendPartnerToken({
      authCode, correlationId, dispatch, getState,
    });
  }
  dispatch(actions.setLoading(false));
};

export const validateUrl = async ({
  urlId, provisionId, dispatch, getState,
}) => {
  const state = getState();
  const options = connectApiFetchOptions({ apiName: 'validateurl', credentials: 'same-origin' }, state, urlId);
  options.init.method = 'get';

  const userSessionApiPath = state.getIn(['config', 'functionsUrl']);

  try {
    const responses = await Promise.all([
      updateUserSession(userSessionApiPath),
      fetch(options.url, options.init),
    ]);

    const data = await responses[1].json();

    if (!responses[1].ok || data.status_code !== '0000') {
      window.sessionStorage.setItem('partnerCancelUrl', data.partner_cancel_url);
      dispatch(actions.mergeValues(fromJS({
        urlId,
        provisionId,
        isAuthenticated: responses[0].ok,
        isAccountsLoaded: true,
        currentFlow: UI_FLOWS.ERROR,
        error: STATUS_CODES.SYSTEM,
      })));
      throw new Error(`validateUrl API failed with status ${responses[1].status}`);
    }

    if (responses[1].ok) {
      window.sessionStorage.setItem('partnerCancelUrl', data.partner_cancel_url);
      dispatch(actions.mergeValues(fromJS({
        urlId,
        provisionId,
        isAuthenticated: responses[0].ok,
        partnerFailureUri: data.partner_failure_url,
        correlationId: responses[1].headers.get('x-amex-tracking-id'),
      })));
    }
  } catch (e) {
    dispatch(actions.mergeValues(fromJS({
      urlId,
      provisionId,
      isAuthenticated: false,
      isAccountsLoaded: true,
      currentFlow: UI_FLOWS.ERROR,
      error: STATUS_CODES.SYSTEM,
    })));
    dispatch(addErrorToReport(e, {
      collectionMethod: 'validateUrl API - Exception',
      moduleID: 'amex-connect-page-wrapper',
    }));
  }
};

export const apiValidateUrl = async ({
  urlId, provisionId, dispatch, getState,
}) => {
  dispatch(actions.setLoading(true));
  /* istanbul ignore next */
  if (urlId === 'mockUrlId' || urlId === 'mockProvisionId') {
    await mockValidateUrl(dispatch);
  } else {
    await validateUrl({
      urlId, provisionId, dispatch, getState,
    });
  }
  dispatch(actions.setLoading(false));
};

export const initiatePushProvisioning = async (dispatch, getState) => {
  const { code, correlationId } = getUrlQueryParams(window.location.search);
  if (code) {
    if (correlationId === undefined) {
      await apiSendPartnerToken({
        authCode: code,
        correlationId,
        dispatch,
        getState,
      });
    }
  }
};

export const initiatePullProvisioning = async (dispatch, getState, partner) => {
  const { urlId, correlationId, provisionId } = getUrlQueryParams(window.location.search);
  let decodedProvisionId;
  if (partner !== 'BPAY') {
    if (urlId) {
      if (correlationId === undefined) {
        await apiValidateUrl({
          urlId,
          provisionId,
          dispatch,
          getState,
        });
      }
    } else {
      decodedProvisionId = provisionId === 'mockProvisionId' ? 'mockProvisionId' : atob(provisionId);
      await apiValidateUrl({
        urlId: decodedProvisionId,
        provisionId,
        dispatch,
        getState,
      });
    }
  } else {
    decodedProvisionId = provisionId === 'mockProvisionId' ? 'mockProvisionId' : atob(provisionId);
    dispatch(actions.mergeValues(fromJS({
      provisionId,
      correlationId: decodedProvisionId,
      isAccountsLoaded: false,
    })));
  }
};

export const initiatePartner = (partner) => async (dispatch, getState) => {
  const payload = {
    partner,
    source: isMobileBrowser() ? 'PARTNER_APP' : 'PARTNER_WEB',
  };
  dispatch(actions.mergeValues(fromJS(payload)));

  const state = getState();
  const partnerConfig = state.getIn(['config', 'partnerConfig', partner]).toJS();

  if (partnerConfig.isPull) {
    await initiatePullProvisioning(dispatch, getState, partner);
  } else {
    await initiatePushProvisioning(dispatch, getState);
  }

  const {
    correlationId, urlId, partnerFailureUri,
  } = getUrlQueryParams(window.location.search);
  if (correlationId) {
    const storage = ApplicationStorage('connect');
    const applicationState = storage.load(correlationId);
    if (applicationState) {
      applicationState.isLoadedFromStorage = true;
      dispatch(actions.mergeValues(fromJS(applicationState)));
    } else {
      dispatch(actions.mergeValues(fromJS({
        correlationId,
        urlId,
        partnerFailureUri,
      })));
    }
  }
};
